import { computed, inject, Injectable } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';

import { resolveCb2PreviewAuthSelectorValues } from '@core/services/cb2-preview/cb2-preview-auth-mock.util';
import { Cb2PreviewStateService } from '@core/services/cb2-preview/cb2-preview-state.service';
import {
  SELECT_AUTH_ERROR,
  SELECT_AUTH_GUARD,
  SELECT_AUTH_PERMISSIONS,
  SELECT_AUTH_STATUS,
  SELECT_AUTH_STATUS_HAS_TOKEN,
  SELECT_AUTH_STATUS_STEP,
  SELECT_AUTH_STATUS_TOKEN,
  SELECT_AUTH_STATUS_WITH_REDIRECT,
  SELECT_AUTH_USER,
  SELECT_AUTH_USER_ID,
  SELECT_AUTH_USER_IS_ANONYMOUS,
  SELECT_AUTH_USER_IS_LOADED,
  SELECT_AUTH_USER_IS_LOADING,
  SELECT_AUTH_USER_IS_LOGGED_IN,
  SELECT_AUTH_USER_META_FIELDS,
} from '@store/authentication/authentication.selection';

@Injectable({ providedIn: 'root' })
export class AuthStoreSelector {
  private readonly store = inject(Store);
  private readonly cb2PreviewState = inject(Cb2PreviewStateService);

  private readonly storeUser = this.store.selectSignal(SELECT_AUTH_USER);
  private readonly storeUserId = this.store.selectSignal(SELECT_AUTH_USER_ID);
  private readonly storeUserIsLoaded = this.store.selectSignal(SELECT_AUTH_USER_IS_LOADED);
  private readonly storeUserIsLoading = this.store.selectSignal(SELECT_AUTH_USER_IS_LOADING);
  private readonly storeUserMetafields = this.store.selectSignal(SELECT_AUTH_USER_META_FIELDS);
  private readonly storeUserIsAnonymous = this.store.selectSignal(SELECT_AUTH_USER_IS_ANONYMOUS);
  private readonly storeUserIsLoggedIn = this.store.selectSignal(SELECT_AUTH_USER_IS_LOGGED_IN);
  private readonly previewActive = this.cb2PreviewState.isActive;

  private readonly authSnapshot = computed(() =>
    resolveCb2PreviewAuthSelectorValues(this.previewActive(), {
      user: this.storeUser(),
      isUserLoaded: this.storeUserIsLoaded(),
      isUserLoading: this.storeUserIsLoading(),
      isLoggedIn: this.storeUserIsLoggedIn(),
      isAnonymous: this.storeUserIsAnonymous(),
      userId: this.storeUserId() ?? undefined,
      metafields: this.storeUserMetafields(),
    }),
  );

  // Error
  selectError = this.store.select(SELECT_AUTH_ERROR);

  // Guard
  readonly selectGuard = this.store.selectSignal(SELECT_AUTH_GUARD);

  // Permission
  selectPermissions = this.store.select(SELECT_AUTH_PERMISSIONS);

  // User — in CB2 preview, always expose a disconnected snapshot so auth modals
  // and visibility rules render as for an anonymous visitor (real Firebase session
  // is left untouched).
  readonly selectUser = computed(() => this.authSnapshot().user);
  readonly selectUser$ = toObservable(this.selectUser);
  selectUserId: Observable<string | undefined> = toObservable(
    computed((): string | undefined => this.authSnapshot().userId ?? undefined),
  );
  readonly selectUserIsLoaded = computed(() => this.authSnapshot().isUserLoaded);
  readonly selectUserIsLoaded$ = toObservable(this.selectUserIsLoaded);
  selectUserIsLoading = toObservable(computed(() => this.authSnapshot().isUserLoading));
  selectUserMetafields = toObservable(computed(() => this.authSnapshot().metafields));
  selectUserIsAnonymous = toObservable(computed(() => this.authSnapshot().isAnonymous));
  selectUserIsLoggedIn = toObservable(computed(() => this.authSnapshot().isLoggedIn));

  // Status
  readonly selectStatus$ = this.store.select(SELECT_AUTH_STATUS);
  readonly selectStatus = this.store.selectSignal(SELECT_AUTH_STATUS);
  selectStatusStep = this.store.select(SELECT_AUTH_STATUS_STEP);
  selectStatusToken = this.store.select(SELECT_AUTH_STATUS_TOKEN);
  readonly selectStatusTokenSignal = this.store.selectSignal(SELECT_AUTH_STATUS_TOKEN);
  selectStatusHasToken = this.store.select(SELECT_AUTH_STATUS_HAS_TOKEN);
  selectStatusWithRedirect = this.store.select(SELECT_AUTH_STATUS_WITH_REDIRECT);
}
